import moment from 'moment';
import React from 'react';
import { ProductEntity } from 'sl-api-connector';
import { AtlasV2PromotionRowType } from 'src/components/AtlasV2Components/AtlasV2PromotionsComponents/promotionTypes';
import AtlasV2ProductListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ProductListContainer';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { promotionTypeToDisplayName } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/constants';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { getColorForMetricChangeValue } from 'src/components/BeaconV2Components/utils/chartStyles';
import { useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import _startCase from 'lodash/startCase';
import _toLower from 'lodash/toLower';
import { useBeaconFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBeaconFilters';

const ProductColumnDefinition = {
  headerName: 'Products',
  tableColumnProps: {
    style: {
      width: '560px',
      paddingRight: '70px'
    }
  },
  valueFormatter: (_, row: { product: ProductEntity }) => (
    <div style={{ width: '464px' }}>
      <ProductCell key={row.product.stacklineSku} stacklineSku={row.product.stacklineSku} title={row.product.title} />
    </div>
  )
};

const AtlasV2PromotionsProductTable = () => {
  const formatMetric = useMetricFormatter();
  const { hasActiveFilters, appFilters } = useBeaconFilters();

  return (
    <AtlasV2ProductListContainer
      iconList={[IconsList.export, IconsList.table]}
      entityTableHeaderProps={{
        title: 'Promotions by Product'
      }}
      buildRequest={(request: AdvancedSearchRequestBuilder) => {
        request
          .makeNullAggregations()
          .addConditionRangeFilter('retailSales', 0.01)
          .setReturnDocuments(true)
          .setProcessDocuments(false)
          .setSearchBy('child')
          .clearSortFields()
          .addSortFieldWithOnlyFieldName('retailSales', 'desc');

        if (hasActiveFilters && appFilters.retailPriceFilter) {
          const minValue = appFilters.retailPriceFilter?.minValue || 0;
          const maxValue = appFilters.retailPriceFilter?.maxValue || 1000000;

          request.addConditionRangeFilter('retailPrice', +minValue, +maxValue);
        }

        return request;
      }}
      useTableDataProps={{
        aggregationKey: 'stacklineSku'
      }}
      showGrid={false}
      groupBy="stacklineSku"
      sortFieldName="retailSales"
      indexName="promotions"
      headColumnDefinition={ProductColumnDefinition}
      additionalColumnDefs={[
        {
          headerName: 'Promotions',
          tableColumnProps: {
            style: {
              paddingLeft: '0px',
              paddingRight: '40px'
            }
          },
          valueFormatter: (_, row: AtlasV2PromotionRowType) => {
            const promotionType = row?.promoType;
            const displayName = promotionTypeToDisplayName[promotionType] || promotionType;
            const dealDate = row?.dealDate;
            const formattedDisplayName = _startCase(_toLower(displayName.split('_')));
            const formattedDate = moment(parseInt(dealDate, 10)).format('MMM-D-YYYY'); // dealDate received as string in some cases

            return (
              <Flex flexDirection="column">
                <Flex sx={{ justifyContent: 'flex-start' }}>
                  <Text variant="body2">{formattedDisplayName}</Text>
                </Flex>
                <Flex sx={{ justifyContent: 'flex-start' }}>
                  <Text variant="body2">{formattedDate}</Text>
                </Flex>
              </Flex>
            );
          }
        },
        {
          headerName: 'Discount',
          tableColumnProps: {
            style: {
              paddingLeft: '40px',
              paddingRight: '40px'
            }
          },
          valueFormatter: (_, row: AtlasV2PromotionRowType) => {
            const promotionPrice = row?.retailPrice ?? 0;
            const retailPriceChange = row?.retailPriceChange ?? 0;
            const originalPrice = promotionPrice - retailPriceChange;
            const percentChange = row?.retailPriceChangePercent;

            return (
              <Flex flexDirection="column">
                <Flex gap={8} sx={{ justifyContent: 'flex-start' }}>
                  <Flex>
                    <Text variant="body2">{formatMetric(promotionPrice, METRICTYPE.MONEY)}</Text>
                  </Flex>
                  <Flex>
                    <Text variant="body2" color={getColorForMetricChangeValue(percentChange)}>
                      {formatMetric(percentChange, METRICTYPE.PERCENT, {
                        showFullValue: true,
                        decimalPlaces: 0
                      })}
                    </Text>
                  </Flex>
                </Flex>
                <Flex sx={{ justifyContent: 'flex-start' }}>
                  <Text variant="body2">{`from ${formatMetric(originalPrice, METRICTYPE.MONEY)}`}</Text>
                </Flex>
              </Flex>
            );
          }
        },
        {
          headerName: 'Sales Lift',
          tableColumnProps: {
            style: {
              paddingLeft: '40px',
              paddingRight: '24px'
            }
          },
          valueFormatter: (_, row: AtlasV2PromotionRowType) => {
            const promotionSales = row?.retailSales ?? 0;
            const retailSalesAmount = row?.retailSalesChange ?? 0;
            const originalSales = +promotionSales - retailSalesAmount;
            const percentChange = row?.retailSalesChangePercent;

            const increaseSign = percentChange > 0 ? '+' : '-';
            return (
              <Flex flexDirection="column">
                <Flex gap={8} sx={{ justifyContent: 'flex-start' }}>
                  <Flex>
                    <Text variant="body2">
                      {formatMetric(+promotionSales, METRICTYPE.MONEY, { showFullValue: false })}
                    </Text>
                  </Flex>
                  <Flex>
                    <Text variant="body2" color={getColorForMetricChangeValue(percentChange)}>
                      {increaseSign}
                      {formatMetric(percentChange, METRICTYPE.PERCENT, {
                        showFullValue: false
                      })}
                    </Text>
                  </Flex>
                </Flex>
                <Flex sx={{ justifyContent: 'flex-start' }}>
                  <Text variant="body2">
                    {`from ${formatMetric(originalSales, METRICTYPE.MONEY, {
                      showFullValue: false
                    })}`}
                  </Text>
                </Flex>
              </Flex>
            );
          }
        }
      ]}
      fields={[{ name: 'unitsSold', displayName: 'Units Sold', displayInTable: false }]}
    />
  );
};

export default AtlasV2PromotionsProductTable;
